import React from 'react';
import PublicLayout from '../components/public/PublicLayout';
import SEO from '../components/common/SEO';
import { seoConfig, generateBreadcrumbSchema } from '../config/seoConfig';
import { Heart, Globe, Users, Award } from 'lucide-react';

// ARCA OFFICIAL COLORS: Primary Green #6B8E23, Dark Green #58761C
const AboutUsPage = () => {
  const breadcrumbs = [
    { name: 'Home', url: '/' },
    { name: 'About Us', url: '/about' }
  ];

  return (
    <PublicLayout>
      <SEO
        title={seoConfig.about.title}
        description={seoConfig.about.description}
        keywords={seoConfig.about.keywords}
        url={seoConfig.about.url}
        structuredData={generateBreadcrumbSchema(breadcrumbs)}
      />
      <div style={{ padding: '3rem 0', backgroundColor: '#FFFFFF' }}>
        <div className="container">
          {/* Header */}
          <div className="row justify-content-center text-center" style={{ marginBottom: '3rem' }}>
            <div className="col-lg-8">
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                backgroundColor: '#6B8E23',
                marginBottom: '1.5rem'
              }}>
                <Heart style={{ color: '#FFFFFF' }} size={40} />
              </div>
              <h1 style={{
                fontSize: '3rem',
                fontWeight: 'bold',
                color: '#6B8E23',
                marginBottom: '1rem'
              }}>Our Story</h1>
              <p style={{
                fontSize: '1.25rem',
                color: '#4A4F4F'
              }}>The journey of building respiratory care excellence across Africa</p>
            </div>
          </div>

          {/* Main Content */}
          <div className="row justify-content-center">
            <div className="col-lg-10">
              {/* Origin Story */}
              <div style={{
                backgroundColor: '#FFFFFF',
                borderRadius: '8px',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                padding: '2.5rem',
                marginBottom: '2rem'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1.5rem' }}>
                  <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '64px',
                    height: '64px',
                    borderRadius: '50%',
                    backgroundColor: '#FFFFFF',
                    border: '3px solid #6B8E23',
                    marginRight: '1rem'
                  }}>
                    <Heart style={{ color: '#6B8E23' }} size={32} />
                  </div>
                  <h2 style={{ fontSize: '1.75rem', fontWeight: 'bold', color: '#6B8E23', margin: 0 }}>
                    Where It All Began
                  </h2>
                </div>
                <div style={{ color: '#4A4F4F', lineHeight: '1.8' }}>
                  <p>
                    The Africa Respiratory Care Association (ARCA) was founded in 2024 by Dorothy H. Bendah, a pioneering respiratory therapist committed to advancing respiratory care across Africa.
                  </p>
                  <p>
                    Dorothy began her journey as one of the pioneering students of Respiratory Therapy at the University of Ghana, where the profession was first introduced in the country. She later earned her Master's degree in Respiratory Therapy from Georgia State University, Atlanta, USA, in 2024, and gained international recognition as a Fellow of the American Association for Respiratory Care (AARC) and the International Council for Respiratory Care (ICRC).
                  </p>
                  <p>
                    Through her education and global exposure, she recognized that respiratory therapy remains a young and underdeveloped profession in most African countries. With formal training programs existing in only a few nations: Ghana, Liberia, Egypt, and Ethiopia, she envisioned a unified platform to promote awareness, education, and professional growth across the continent.
                  </p>
                  <p style={{ marginBottom: 0 }}>
                    This vision led to the creation of ARCA. Today, the association brings together over 2,000 members across more than 20 African countries, working collectively to establish respiratory therapy as a recognized profession in universities and healthcare systems throughout Africa.
                  </p>
                </div>
              </div>

              {/* Impact Cards - ALL ARCA GREEN */}
              <div className="row g-3 g-md-4" style={{ marginBottom: '2rem' }}>
                <div className="col-12 col-sm-6 col-md-4">
                  <div style={{
                    backgroundColor: '#6B8E23',
                    color: '#FFFFFF',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px rgba(107, 142, 35, 0.3)',
                    padding: '2rem',
                    textAlign: 'center',
                    height: '100%'
                  }}>
                    <Users size={48} style={{ marginBottom: '1rem', color: '#FFFFFF' }} />
                    <h3 style={{ fontSize: '3rem', fontWeight: 'bold', marginBottom: '0.5rem', color: '#FFFFFF' }}>2,000+</h3>
                    <p style={{ margin: 0, color: '#FFFFFF' }}>Active Members</p>
                  </div>
                </div>
                <div className="col-12 col-sm-6 col-md-4">
                  <div style={{
                    backgroundColor: '#6B8E23',
                    color: '#FFFFFF',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px rgba(107, 142, 35, 0.3)',
                    padding: '2rem',
                    textAlign: 'center',
                    height: '100%'
                  }}>
                    <Globe size={48} style={{ marginBottom: '1rem', color: '#FFFFFF' }} />
                    <h3 style={{ fontSize: '3rem', fontWeight: 'bold', marginBottom: '0.5rem', color: '#FFFFFF' }}>20+</h3>
                    <p style={{ margin: 0, color: '#FFFFFF' }}>Countries</p>
                  </div>
                </div>
                <div className="col-12 col-sm-6 col-md-4">
                  <div style={{
                    backgroundColor: '#6B8E23',
                    color: '#FFFFFF',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px rgba(107, 142, 35, 0.3)',
                    padding: '2rem',
                    textAlign: 'center',
                    height: '100%'
                  }}>
                    <Award size={48} style={{ marginBottom: '1rem', color: '#FFFFFF' }} />
                    <h3 style={{ fontSize: '3rem', fontWeight: 'bold', marginBottom: '0.5rem', color: '#FFFFFF' }}>25+</h3>
                    <p style={{ margin: 0, color: '#FFFFFF' }}>Trainings and Webinars</p>
                  </div>
                </div>
              </div>

              {/* Looking Forward - ARCA GREEN ONLY */}
              <div style={{
                backgroundColor: '#6B8E23',
                color: '#FFFFFF',
                borderRadius: '8px',
                boxShadow: '0 4px 6px rgba(107, 142, 35, 0.3)',
                padding: '2.5rem'
              }}>
                <h2 style={{ fontSize: '1.75rem', fontWeight: 'bold', marginBottom: '1rem', color: '#FFFFFF' }}>Looking Forward</h2>
                <p style={{ fontSize: '1.25rem', marginBottom: '1.5rem', color: '#FFFFFF' }}>
                  As we continue to grow, our commitment to excellence in respiratory care remains unwavering. We envision a future where:
                </p>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  <li style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '1rem' }}>
                    <span style={{ color: '#FFFFFF', marginRight: '1rem', fontSize: '1.25rem' }}>✓</span>
                    <span style={{ color: '#FFFFFF' }}>Every African nation has a thriving respiratory care professional community</span>
                  </li>
                  <li style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '1rem' }}>
                    <span style={{ color: '#FFFFFF', marginRight: '1rem', fontSize: '1.25rem' }}>✓</span>
                    <span style={{ color: '#FFFFFF' }}>Access to quality respiratory care is a reality for all, regardless of location</span>
                  </li>
                  <li style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '1rem' }}>
                    <span style={{ color: '#FFFFFF', marginRight: '1rem', fontSize: '1.25rem' }}>✓</span>
                    <span style={{ color: '#FFFFFF' }}>African respiratory care professionals are globally recognized leaders in the field</span>
                  </li>
                  <li style={{ display: 'flex', alignItems: 'flex-start' }}>
                    <span style={{ color: '#FFFFFF', marginRight: '1rem', fontSize: '1.25rem' }}>✓</span>
                    <span style={{ color: '#FFFFFF' }}>Research and innovation in respiratory care flourishes across the continent</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
};

export default AboutUsPage;
